import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import GenerateButton from "../components/GenerateButton/GenerateButton";
import {
  getStoredGenerateParams,
  buildGenerateQueryString,
  normalizeGenerateParams,
} from "../utils/generationParams";
import styles from "../styles/Landing.module.css";

export default function Landing() {
  const [useEraFilter, setUseEraFilter] = useState(false);
  const [selectedEra, setSelectedEra] = useState("");
  const [useJamControl, setUseJamControl] = useState(false);
  const [jamminess, setJamminess] = useState(50);
  const [hasSetlist, setHasSetlist] = useState(false);
  const [useSameShowSegues, setUseSameShowSegues] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if there's a saved setlist
    const savedSetlist = localStorage.getItem("lastSetlist");
    setHasSetlist(!!savedSetlist);

    const stored = normalizeGenerateParams(getStoredGenerateParams() || {});
    if (stored.era) {
      setUseEraFilter(true);
      setSelectedEra(stored.era);
    }
    if (stored.jamminess !== undefined) {
      setUseJamControl(true);
      setJamminess(Math.round(stored.jamminess * 100));
    }
    if (stored.same_show_segues !== undefined) {
      setUseSameShowSegues(Boolean(stored.same_show_segues));
    }
  }, []);

  const handleReturnToSetlist = () => {
    const savedSetlist = localStorage.getItem("lastSetlist");
    if (savedSetlist) {
      try {
        const setlist = JSON.parse(savedSetlist);
        navigate("/setlist", { state: { setlist } });
      } catch (err) {
        console.error("Failed to parse saved setlist:", err);
      }
    }
  };

  const handleGenerate = () => {
    const controlParams = {
      same_show_segues: useSameShowSegues,
    };

    if (useEraFilter && selectedEra) {
      controlParams.era = selectedEra;
    }

    if (useJamControl) {
      const normalizedValue = Number((jamminess / 100).toFixed(2));
      controlParams.jamminess = normalizedValue;
    }

    const storedParams = getStoredGenerateParams();

    const clearsStoredFilters =
      (!!storedParams?.era && !useEraFilter) ||
      (storedParams?.jamminess !== undefined && !useJamControl) ||
      (storedParams?.same_show_segues !== undefined &&
        storedParams.same_show_segues !== useSameShowSegues);

    const hasExplicitControls =
      useEraFilter ||
      useJamControl ||
      useSameShowSegues !== true ||
      clearsStoredFilters;

    const paramsToUse =
      hasExplicitControls || !hasSetlist || !storedParams
        ? controlParams
        : storedParams;

    const queryString = buildGenerateQueryString(paramsToUse);
    navigate(`/generate${queryString ? `?${queryString}` : ""}`);
  };

  const getJamLabel = (value) => {
    if (value < 25) return "Tight & Tidy";
    if (value < 50) return "Easy Does It";
    if (value < 75) return "Run of the Mill";
    if (value < 90) return "Pretty Jammy";
    return "FULL SEND 🚀";
  };

  const getJamWarning = (value) => {
    const normalized = value / 100;
    if (normalized > 0.666) {
      return {
        text: "⚠️ Dial at your own risk. Set length and song choice becomes more unstable as this increases.",
        className: styles.jamWarningHigh,
      };
    }
    if (normalized < 0.333) {
      return {
        text: "😴 Turning this down may result in more songs, but less jams. But expect some weirdness too.",
        className: styles.jamWarningLow,
      };
    }
    return null;
  };

  const normalized = (jamminess / 100).toFixed(2);
  const warning = getJamWarning(jamminess);

  return (
    <div className={styles.wrapper}>
      <div className={styles.container}>
        <h1 className={styles.title}>🎸 Inphinite</h1>
        <div className={styles.subtitle}>The Phish Setlist Generator</div>
        <p className={styles.description}>
          Generate authentic Phish setlists powered by ML and 40+ years of
          historical data from Phish.in.
        </p>

        {hasSetlist && (
          <button className={styles.btn} onClick={handleReturnToSetlist}>
            Return to Setlist
          </button>
        )}

        <GenerateButton
          hasSetlist={hasSetlist}
          variant="landing"
          onClick={handleGenerate}
        />

        <div className={styles.controlsContainer}>
          <label className={styles.toggle}>
            <input
              type="checkbox"
              checked={useSameShowSegues}
              onChange={(e) => setUseSameShowSegues(e.target.checked)}
            />
            <span>Same Show Segues</span>
          </label>

          {useSameShowSegues && (
            <div className={styles.selector}>
              <p className={styles.seguesMessage}>
                Use same show segues when possible (and allow for chance for fun
                rarer segues)
              </p>
            </div>
          )}
        </div>

        <div className={styles.controlsContainer}>
          <label className={styles.toggle}>
            <input
              type="checkbox"
              checked={useEraFilter}
              onChange={(e) => setUseEraFilter(e.target.checked)}
            />
            <span>Filter by Era</span>
          </label>

          {useEraFilter && (
            <div className={styles.selector}>
              <select
                className={styles.dropdown}
                value={selectedEra}
                onChange={(e) => setSelectedEra(e.target.value)}
              >
                <option value="">All Eras (1983-present)</option>
                <option value="1.0">1.0 (1983-1999)</option>
                <option value="2.0">2.0 (2000-2004)</option>
                <option value="3.0">3.0 (2009-2021)</option>
                <option value="4.0">4.0 (2021+)</option>
              </select>
            </div>
          )}
        </div>

        <div className={styles.controlsContainer}>
          <label className={styles.toggle}>
            <input
              type="checkbox"
              checked={useJamControl}
              onChange={(e) => setUseJamControl(e.target.checked)}
            />
            <span>Control the Jam Dial</span>
          </label>

          {useJamControl && (
            <div className={styles.selector}>
              <div className={styles.sliderContainer}>
                <input
                  type="range"
                  className={styles.slider}
                  min="0"
                  max="100"
                  value={jamminess}
                  onChange={(e) => setJamminess(parseInt(e.target.value, 10))}
                />
                <div className={styles.jamLabels}>
                  <span className={styles.jamLabel}>🎯 Tight</span>
                  <span className={styles.jamLabel}>🎸 Balanced</span>
                  <span className={styles.jamLabel}>🚀 Full Send</span>
                </div>
                <div className={styles.jamValue}>
                  {getJamLabel(jamminess)} ({normalized})
                </div>
                {warning && (
                  <div className={`${styles.jamWarning} ${warning.className}`}>
                    {warning.text}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <hr className={styles.divider} />

        <p className={styles.featuresLink}>
          <Link to="/features">What exactly can this do? →</Link>
        </p>

        <div className={styles.attribution}>
          All inspiration and helpful amounts of data and existing work from the
          folks at{" "}
          <a href="https://phish.in" target="_blank" rel="noopener noreferrer">
            Phish.in
          </a>
          . Please support what they do!
        </div>
      </div>
    </div>
  );
}
